import * as React from 'react'
import { cn } from '@/lib/utils'
import { Label } from './label'

/** Label + control + hint/error, wired up for a11y (`aria-describedby`, `aria-invalid`). */
export function Field({
  label,
  htmlFor,
  error,
  hint,
  required,
  className,
  children,
  optional,
}: {
  label?: React.ReactNode
  htmlFor?: string
  error?: string
  hint?: React.ReactNode
  required?: boolean
  optional?: boolean
  className?: string
  children: React.ReactElement<Record<string, unknown>>
}) {
  const generatedId = React.useId()
  const id = htmlFor ?? generatedId
  const messageId = `${id}-message`
  const control = React.cloneElement(children, {
    id,
    'aria-invalid': error ? true : undefined,
    'aria-describedby': error || hint ? messageId : undefined,
  })
  return (
    <div className={cn('grid gap-1.5', className)}>
      {label && (
        <Label htmlFor={id}>
          {label}
          {required && <span className="text-destructive" aria-hidden>*</span>}
          {optional && <span className="font-normal text-muted-foreground">(optional)</span>}
        </Label>
      )}
      {control}
      {error ? (
        <p id={messageId} role="alert" className="text-[12.5px] font-medium text-destructive">
          {error}
        </p>
      ) : hint ? (
        <p id={messageId} className="text-[12.5px] text-muted-foreground">
          {hint}
        </p>
      ) : null}
    </div>
  )
}

export function FormError({ message, className }: { message?: string; className?: string }) {
  if (!message) return null
  return (
    <div role="alert" className={cn('rounded-lg border border-destructive/30 bg-destructive/8 px-3 py-2 text-sm text-destructive', className)}>
      {message}
    </div>
  )
}
